import { parseJsonConf } from "OK/utils/parseJsonConf";
import { myTrackerService } from "./MyTrackerService";
import { TimespentTrackerService } from "./TimespentTrackerService";
import { ActivityType } from "./TimespentTrackerTypes"
import { debugLog } from "./utils"
import type { ActivityId, ActivityMap } from "./TimespentTrackerTypes"
import type { IVideoDocumentEvent } from "@ok/interfaces/video/IVideoPlayer";

const DEFAULT_LAYER_RESET_TIME = 100;
const DEFAULT_PROGRESS_EVENT_IGNORE_TIME = 1_000;

let lastActivityType: ActivityType | null = null;

export default class TimespentTracker {
    timespentAccountId!: number;
    defaultActivityType!: ActivityType;

    withPlayerLogging: boolean = false;
    isTimespentStarted = false;
    needCheckProgress = true;
    playerViaLayer = false;
    currentMovieId: string | null = null;
    isMovieEnd = false;
    progressEventIgnoreTime!: number;

    currentStateId!: string;
    currentLayerId: string | null = null;
    prevLayerId: string | null = null;
    currentActivityType!: ActivityType;
    prevActivityType!: ActivityType;

    typeToId!: Record<ActivityType, ActivityId>;
    stateIdToType!: ActivityMap<string, ActivityType>;
    layerIdToType!: ActivityMap<string, ActivityType>;

    layerWasJustReset = false;
    layerResetTime!: number;

    public activate(el: HTMLElement): void {
        this.timespentAccountId = Number(el.dataset.timespentAccountId);
        this.layerResetTime = el.dataset.layerResetTime ? Number(el.dataset.layerResetTime) : DEFAULT_LAYER_RESET_TIME;
        this.progressEventIgnoreTime = el.dataset.progressEventIgnoreTime ? Number(el.dataset.progressEventIgnoreTime) : DEFAULT_PROGRESS_EVENT_IGNORE_TIME;
        this.defaultActivityType = <ActivityType>el.dataset.defaultActivityType;
        this.withPlayerLogging = el.dataset.withPlayerLogging === 'true';

        this.typeToId = parseJsonConf(el, 'data-type-to-id')!;

        const typeToStateId = parseJsonConf<ActivityMap<ActivityType>>(el, 'data-type-to-state-id');
        this.stateIdToType = this.revertMap(typeToStateId);

        const typeToLayerId = parseJsonConf<ActivityMap>(el, 'data-type-to-layer-id');
        this.layerIdToType = this.revertMap(typeToLayerId);

        this.currentStateId = el.dataset.currentStateId!;

        OK.loader.use('OKCustomJs', () => this.delayInitForLayers());
    }

    public deactivate(): void {
        OK.Layers.unsubscribeGlobal(this.onLayerToggle);
        this.stopTimespentByType(lastActivityType!);

        if (this.withPlayerLogging) {
            if (this.isTimespentStarted) {
                this.stopPlayerTimespent();
            }

            document.removeEventListener('__videoPlayerEvent', this.onVideoPlayerEvent);
            document.removeEventListener('adv', this.onVideoPlayerEvent);
        }
    }

    private delayInitForLayers = (): void => {
        this.currentLayerId = OK.Layers.getTopLayerId();
        this.refreshActivityTypes();

        OK.Layers.subscribeGlobal(this.onLayerToggle);

        this.debugLog(`Init finished! Current stateId: ${this.currentStateId}`);

        this.processState(this.prevActivityType);

        if (this.withPlayerLogging) {
            document.addEventListener('__videoPlayerEvent', this.onVideoPlayerEvent);
            // Событие старта рекламы в плеере
            document.addEventListener('adv', this.onVideoPlayerEvent);
        }
    };

    private onLayerToggle = (): void => {
        const prevType = this.currentActivityType;
        const intermediateLayerId = OK.Layers.getTopLayerId();

        this.layerWasJustReset = false;

        // При переключении лееров сначала прилетает событие с новым layer'ом равным null,
        // поэтому чтоб это не ломало логику - кешируем значение и сравниваем его через момент времени.
        // Используем макротаски. Должно корректно работать 3 кейса: открытие леера, переключение и закрытие.

        if (intermediateLayerId === null) {
            // Сюда заходим при закрытии и в 1 итерацию переключения
            setTimeout(() => {
                if (!this.layerWasJustReset) {
                    // Сюда заходим при переключении. В 3 итерацию
                    this.prevLayerId = this.currentLayerId;
                    this.currentLayerId = intermediateLayerId;
                }
                this.performLayerChange(prevType);
            }, this.layerResetTime);
        } else {
            // Сюда заходим при открытии и в 2 итерацию переключения
            this.prevLayerId = this.currentLayerId;
            this.currentLayerId = intermediateLayerId;
            this.layerWasJustReset = true;
            setTimeout(() => {
                if (this.layerWasJustReset) {
                    this.performLayerChange(prevType);
                }
            }, this.layerResetTime);
        }
    }

    private onVideoPlayerEvent = (e: Event): void => {
        const event = e as CustomEvent<IVideoDocumentEvent>;
        this.isMovieEnd = event.detail.data.duration == event.detail.data.value;

        switch (event.detail.name) {
            case "started":
            case "play":
                if (!this.isTimespentStarted) {
                    this.currentMovieId = event.detail.data.movieId;
                    this.startPlayerTimespent();
                }
                break;
            case "pause":
                this.stopPlayerTimespent();
                break;
            case "progress":
                if (!this.needCheckProgress) {
                    break;
                }
                // Конец видео
                if (this.isMovieEnd) {
                    this.stopPlayerTimespent();
                // Продолжаем смотреть видео с определённой точки
                } else if (this.currentMovieId === event.detail.data.movieId && !this.isTimespentStarted) {
                    this.startPlayerTimespent();
                }
                break;
        }
    }

    private performLayerChange(prevType: ActivityType): void {
        this.refreshActivityTypes();
        this.debugLog(`layerId change! Current layerId: ${this.currentLayerId}. CurrentType: ${this.currentActivityType}, prevType: ${prevType}`);

        if (this.currentActivityType !== prevType) {
            this.stopTimespentByType(prevType);
            this.startTimespentByType(this.currentActivityType);
        }
    }

    private processState(prevType?: ActivityType | null, lastPingTimestamp?: number | null): void {
        const activityTypeHasChanged = prevType !== this.currentActivityType;

        if (!lastActivityType) {
            lastActivityType = this.currentActivityType;
            this.startTimespentByType(lastActivityType);
            return;
        }

        if (activityTypeHasChanged) {
            this.prevActivityType = prevType!;
            // Логирование новой сессии при смене типа активности
            this.stopTimespentByType(lastActivityType);
            lastActivityType = this.getCurrentActivityType();
            this.startTimespentByType(lastActivityType);
            return;
        }
    }

    private getCurrentActivityType() {
        return this.stateIdToType?.[this.currentStateId]?.[0] || this.defaultActivityType;
    }

    private refreshActivityTypes(): void {
        this.prevActivityType = this.currentActivityType;
        this.playerViaLayer = false;

        if (this.currentLayerId) {
            // Приоритизируем layerId над stateId для определения типа активности
            const layerTypes = this.layerIdToType[this.currentLayerId];
            if (layerTypes?.length) {
                // Делаем пометку, что плеер сейчас проигрывается в леере. Под леером он должен выключаться
                this.playerViaLayer = true;
                this.currentActivityType = layerTypes[0];
                return;
            }
            this.debugLog(`current layerId "${this.currentLayerId}" is not binded to type!`);
            this.currentActivityType = this.defaultActivityType;
            return;
        }

        if (this.stateIdToType[this.currentStateId]?.length) {
            this.currentActivityType = this.getCurrentActivityType();
            return;
        }

        this.currentActivityType = this.defaultActivityType;
    }

    private revertMap<K extends string = string, V extends string = string>(activityMap: ActivityMap | null): ActivityMap<K, V> {
        if (!activityMap) {
            return <ActivityMap<K, V>>{};
        }
        const map = <ActivityMap<K, V>>{};

        for (let type in activityMap) {
            activityMap[type].forEach((layerId) => {
                if (map[layerId as K]) {
                    map[layerId as K].push(type as V);
                } else {
                    map[layerId as K] = [type as V];
                }
            });
        }
        return map;
    }

    private startPlayerTimespent(): void {
        TimespentTrackerService.startPlayerTimespent(this.timespentAccountId);
        this.isTimespentStarted = true;
    }

    private stopPlayerTimespent(): void {
        this.stopTimespentByType(ActivityType.PLAYER);
        this.needCheckProgress = false;
        this.isTimespentStarted = false;

        // Игнорируем "progress" event'ы после паузы или окончания
        setTimeout(() => {
            this.needCheckProgress = true;
        }, DEFAULT_PROGRESS_EVENT_IGNORE_TIME);
    }

    private startTimespentByType(activityType: ActivityType): void {
        TimespentTrackerService.startTimespent(this.typeToId[activityType], this.timespentAccountId);
    }

    private stopTimespentByType(activityType: ActivityType): void {
        TimespentTrackerService.stopTimespent(this.typeToId[activityType], this.timespentAccountId);
    }

    private debugLog(...msgArgs: unknown[]): void {
        debugLog(myTrackerService.serviceName, '[timespent]', ...msgArgs);
    }
}
